import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services'
import { queryKeys, invalidateQueries } from '@/lib/query'
import type { User, Role } from '@/types'

interface CreateUserData {
  email: string
  name: string
  role: Role
}

interface UpdateUserData {
  email?: string
  name?: string
  role?: Role
}

/**
 * Hook to fetch all users for an organization.
 */
export function useUsers(orgId: string) {
  return useQuery({
    queryKey: queryKeys.users.all(orgId),
    queryFn: () => api.get<User[]>('/users'),
    enabled: !!orgId,
  })
}

/**
 * Hook to fetch a single user by ID.
 */
export function useUser(userId: string) {
  return useQuery({
    queryKey: queryKeys.users.detail(userId),
    queryFn: () => api.get<User>(`/users/${userId}`),
    enabled: !!userId,
  })
}

/**
 * Hook to create a new user.
 */
export function useCreateUser(orgId: string) {
  return useMutation({
    mutationFn: (data: CreateUserData) => api.post<User>('/users', data),
    onSuccess: () => {
      invalidateQueries.users(orgId)
    },
  })
}

/**
 * Hook to update a user.
 */
export function useUpdateUser(userId: string, orgId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateUserData) =>
      api.put<User>(`/users/${userId}`, data),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(queryKeys.users.detail(userId), updatedUser)
      invalidateQueries.users(orgId)
    },
  })
}

/**
 * Hook to delete a user.
 */
export function useDeleteUser(orgId: string) {
  return useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/users/${userId}`),
    onSuccess: () => {
      invalidateQueries.users(orgId)
    },
  })
}

/**
 * Hook to reset a user's password.
 */
export function useResetUserPassword() {
  return useMutation({
    mutationFn: (userId: string) =>
      api.post<void>(`/users/${userId}/reset-password`, {}),
  })
}

/**
 * Hook to resend an invitation email to a user.
 */
export function useResendUserInvite() {
  return useMutation({
    mutationFn: (userId: string) =>
      api.post<void>(`/users/${userId}/resend-invite`, {}),
  })
}
